import styled from "@emotion/styled";
import { colors } from "@ui/style/colors";
import { REGEX_EMAIL } from "@utils/regex-utils";
import { InputText, InputTextProps } from "primereact/inputtext";
import { InputTextarea, InputTextareaProps } from "primereact/inputtextarea";
import { Password, PasswordProps } from "primereact/password";
import { classNames } from "primereact/utils";
import React, { InputHTMLAttributes } from "react";
import { Control, Controller, Path } from "react-hook-form";

interface TextInputProps extends InputTextProps {
	error?: boolean;
}

export const TextInput = React.forwardRef<HTMLInputElement, TextInputProps>(({ error, className, ...props }, ref) => (
	<InputText ref={ref} className={classNames({ "p-invalid": !!error }, className)} {...props} />
));
TextInput.displayName = "TextInput";

interface TextAreaProps extends InputTextareaProps {
	error?: boolean;
}

export const TextArea = React.forwardRef<HTMLTextAreaElement, TextAreaProps>(({ error, className, ...props }, ref) => (
	<InputTextarea ref={ref} className={classNames({ "p-invalid": !!error }, className)} {...props} />
));
TextArea.displayName = "TextArea";

export const PasswordInput = React.forwardRef<Password, PasswordProps & { error?: boolean }>(
	({ error, className, inputRef, ...props }) => (
		<Password
			inputRef={inputRef}
			toggleMask
			feedback={false}
			className={classNames({ "p-invalid": !!error }, className)}
			{...props}
		/>
	),
);
PasswordInput.displayName = "PasswordInput";

interface FormEmailInputProps<U extends { email: string }> {
	wording: { requiredError: string; formatError: string; placeholder?: string };
	control?: Control<U, object>;
	error?: string;
}

export const FormEmailInput = <U extends { email: string }>({
	control,
	error,
	wording,
}: FormEmailInputProps<U>): JSX.Element => (
	<Controller
		name={"email" as Path<U>}
		control={control}
		rules={{
			required: wording.requiredError,
			pattern: {
				value: REGEX_EMAIL,
				message: wording.formatError,
			},
		}}
		render={({ field, fieldState }) => (
			<TextInput
				{...field}
				placeholder={wording.placeholder}
				error={fieldState.invalid || !!error}
				inputMode="email"
				autoCorrect="off"
				autoCapitalize="none"
			/>
		)}
	/>
);

export const SmallTextInput = React.forwardRef<HTMLInputElement, InputHTMLAttributes<HTMLInputElement>>(
	({ className, ...props }, ref) => <Input ref={ref} className={classNames("p-size-s-bold", className)} {...props} />,
);
SmallTextInput.displayName = "SmallTextInput";

const Input = styled.input`
	appearance: none;
	border-radius: 7px;
	padding: 0;
	max-width: 1.2rem;
	height: 1.375rem;

	border: none;

	background-color: ${colors.primaryBackground};
	color: ${colors.grey};

	&:focus {
		outline: 0;
	}
`;
